import create from 'zustand'
import {
	fetchMarketPricings,
	fetchMarketPricing,
	deleteMarketPricing,
	upsertMarketPricing,
} from './pricingApi'

export const usePricingStore = create((set, get) => ({
	markets: [],
	loadingMarkets: false,
	hasMarketsInitiallyLoaded: false,
	activePricingType: 'COMMERCIAL',

	setActivePricingType: async (t) => {
		if (t === get().activePricingType) return
		set({ activePricingType: t, markets: [] })
		await get().loadMarkets()
	},

	loadMarkets: async () => {
		const { loadingMarkets, activePricingType } = get()
		if (loadingMarkets) return
		set({ loadingMarkets: true })
		try {
			const data = await fetchMarketPricings(activePricingType)
			set({ markets: data || [], hasMarketsInitiallyLoaded: true })
		} catch (e) {
			console.error('Failed to load markets', e)
		} finally {
			set({ loadingMarkets: false })
		}
	},

	refreshMarket: async (marketCode) => {
		const { activePricingType, markets } = get()
		if (!marketCode) return
		try {
			const data = await fetchMarketPricing(marketCode, activePricingType)
			if (!data) {
				return
			}
			const idx = markets.findIndex((m) => m.market === marketCode)
			let newMarkets
			if (idx >= 0) {
				newMarkets = [...markets]
				newMarkets[idx] = data
			} else {
				newMarkets = [...markets, data]
			}
			set({ markets: newMarkets })
		} catch (e) {
			console.error('Failed to refresh market', marketCode, e)
		}
	},

	deleteMarket: async (market) => {
		const { activePricingType, markets } = get()
		if (!market) return
		try {
			await deleteMarketPricing(market, activePricingType)
			set({ markets: markets.filter((m) => m.market !== market) })
		} catch (e) {
			console.error('Failed to delete market', market, e)
			throw e
		}
	},

	upsertActiveMarketPricing: async (market, fields) => {
		const { activePricingType, markets } = get()
		if (!market) throw new Error('Market code required')
		if (fields.licensePrice == null || fields.licensePrice === '')
			throw new Error('License price required')
		if (!fields.currency) throw new Error('Currency required')
		const payload = {
			currency: String(fields.currency).toUpperCase(),
			licensePrice: Number(fields.licensePrice),
			compensation:
				fields.compensation === '' || fields.compensation == null
					? null
					: Number(fields.compensation),
			furtherDamages:
				fields.furtherDamages === '' || fields.furtherDamages == null
					? null
					: Number(fields.furtherDamages),
			manipulatedFee:
				fields.manipulatedFee === '' || fields.manipulatedFee == null
					? null
					: Number(fields.manipulatedFee),
			ceaseAndDesistFee:
				fields.ceaseAndDesistFee === '' || fields.ceaseAndDesistFee == null
					? null
					: Number(fields.ceaseAndDesistFee),
			reminderOneFee:
				fields.reminderOneFee === '' || fields.reminderOneFee == null
					? null
					: Number(fields.reminderOneFee),
			reminderTwoFee:
				fields.reminderTwoFee === '' || fields.reminderTwoFee == null
					? null
					: Number(fields.reminderTwoFee),
			finalWarningFee:
				fields.finalWarningFee === '' || fields.finalWarningFee == null
					? null
					: Number(fields.finalWarningFee),
			administrativeFee:
				fields.administrativeFee === '' || fields.administrativeFee == null
					? null
					: Number(fields.administrativeFee),
		}
		const upperMarket = market.toUpperCase()
		const row = await upsertMarketPricing(
			upperMarket,
			activePricingType,
			payload,
		)
		const idx = markets.findIndex((m) => m.market === upperMarket)
		const newRow = row // already includes pricingType
		let newMarkets
		if (idx >= 0) {
			newMarkets = [...markets]
			newMarkets[idx] = newRow
		} else {
			newMarkets = [...markets, newRow]
		}
		set({ markets: newMarkets })
		return newRow
	},
}))
